import SearchInput from '@components/Shared/SearchInput';
import { MAX_SEARCH_LENGTH } from '@lib/inspections/filters';
import { type SxProps, type Theme } from '@mui/material';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

interface SearchFieldProps {
  /** The search in force, as the URL holds it. */
  value: string;
  /** Called once the reader pauses. */
  onCommit: (text: string) => void;
  placeholder: string;
  /** How many rows the list holds, shown in the box while something is typed. */
  count: number;
  sx?: SxProps<Theme>;
}

/** Long enough to let a word be typed, short enough that the list follows the typing. */
const SEARCH_DELAY = 300;

/**
 * The name search of the inspections page: the app's own search box, as the Projects page
 * draws it, with what is typed held here and handed on once the reader pauses, so the URL
 * and the request follow words rather than key presses. A search set from outside, such as
 * the browser's Back, replaces what is in the box. Clearing every filter mounts the box anew
 * (the page keys it by the clears), which drops what was typed and a commit still waiting:
 * the search in force was often empty before the clear and after it, so nothing here would
 * see it happen.
 */
const SearchField: React.FC<SearchFieldProps> = ({ value, onCommit, placeholder, count, sx }) => {
  const [draft, setDraft] = useState(value);
  const [takenFrom, setTakenFrom] = useState(value);
  const timer = useRef<number | undefined>(undefined);

  // The latest callback, not the one of the render the key was pressed in: that one spreads
  // the filters as they were then, so a filter set during the pause would be written over.
  const commit = useRef(onCommit);

  useLayoutEffect(() => {
    commit.current = onCommit;
  });

  // A value that arrives from outside wins over what was typed; adjusted while rendering, as
  // React advises for state that follows a prop, so the box never shows the old text a frame.
  if (value !== takenFrom) {
    setTakenFrom(value);
    setDraft(value);
  }

  // And over a commit still waiting: a clear pressed mid-word must not be undone by the word.
  useEffect(() => window.clearTimeout(timer.current), [value]);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  // The box holds no more than the endpoint takes, so a long paste is cut here rather than
  // sent and refused.
  const commitLater = (typed: string) => {
    const text = typed.slice(0, MAX_SEARCH_LENGTH);

    setDraft(text);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => commit.current(text), SEARCH_DELAY);
  };

  return <SearchInput text={draft} action={commitLater} count={count} placeholder={placeholder} sx={sx} />;
};

export default SearchField;
